import React from "react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Outlet,
} from "react-router-dom";
import Login from "../pages/NotProtectedOnly/Login";
import ForgotPassword from "../pages/NotProtectedOnly/ForgotPassword";
import VerifyOtp from "../pages/NotProtectedOnly/VerifyOtp";
import Companies from "../pages/Companies";
import CompanyView from "../pages/CompanyView";
import { Route } from "react-router-dom";
import { About } from "../pages/About";
import { Contact } from "../pages/Contact";
import { ProtectedRoute } from "../layouts/ProtectedRoutes";
import { RoleBasedRoute } from "../layouts/RoleBasedRoute";
import { GlobalErrorElement } from "../components/GlobalErrorElement.jsx";
import { ErrorBoundary } from "react-error-boundary";
import { NotProtectedOnlyRoutes } from "../layouts/NotProtectedOnlyRoutes.jsx";
import { Layout } from "../layouts/Layout.jsx";
import CompanyForm from "@/components/Companies/CompanyForm";
import CampaignForm from "@/components/Companies/CampaignForm";
import VacancyForm from "@/components/Vacancies/VacancyForm";
import Forms from "../pages/Forms";
import Users from "@/pages/protected/Users/Users.jsx"
import Recruiters from "@/pages/protected/Recruiters.jsx";
import ArchivedCampaign from "../pages/archived/Campaign.jsx";
import AllCampaign from "../pages/all/Campaign.jsx";
import ArchivedVacancy from "../pages/archived/Vacancy.jsx";
import AllVacancies from "../pages/all/Vacancy.jsx";
import FormView from '../pages/FormView';
import { FormBuilder } from '../components/FormBuilder/FormBuilder';
import Vacancies from "@/components/Companies/Vacancies";
import CampaignVacancies from "@/components/Companies/CampaignVacancies";
import CompanyDashboard from "../pages/protected/Company/Dashboard";
import AdminDashboard from "../pages/protected/Admin/Dashboard";
import RecruiterAdminDashboard from "../pages/protected/Company/RecruiterAdminDashboard";

import { default as FrontLayout } from "@/components/Frontend/Layout.jsx";
import { default as ApplyForm } from "@/components/Frontend/components/CampaignApplyForm.jsx";
import NotFound from "@/pages/NotProtectedOnly/404.jsx";
import SuccessPage from "@/components/Frontend/components/SuccessPage.jsx";
import PreScreenView from "@/pages/PreScreen/PreScreenView.jsx";
import ApplicationReviewContainer from "@/pages/PreScreen/ApplicationReviewContainer.jsx";
import { FullScreenLayout } from "@/layouts/FullScreenLayout.jsx";
import Candidates from "@/pages/Candidate.jsx";
import SettingsList from "@/pages/protected/settings.jsx";
import Marketing from "@/pages/protected/Marketing.jsx";
import CreateMarketingCampaign from "@/pages/protected/CreateMarketingCampaign.jsx";
import CandidateDetails from "@/pages/protected/Candidates/candidateDetails.jsx";
import CandidateDashboard from "@/pages/protected/Candidates/CandidateDashboard.jsx";
import CandidateApplications from "@/pages/protected/Candidates/CandidateApplications.jsx";
import CandidateApplicationView from "@/pages/protected/Candidates/CandidateApplicationView.jsx";
import ResetPassword from "@/pages/NotProtectedOnly/ResetPassword.jsx";
import Notes from "@/pages/protected/Notes/Notes.jsx";
import VacancyStatuses from "@/pages/VacancyStatuses";
import StageBuilder from "@/components/VacancyStatuses/StageBuilder";
import NoteTemplates from "@/pages/NoteTemplates";
import InterviewSlotsUpload from "@/pages/protected/InterviewSlotsUpload.jsx";
import CreateEventSlot from "@/pages/protected/CreateEventSlot.jsx";
import InterviewSlotDetails from "@/pages/protected/InterviewSlotDetails.jsx";
import ContactRequests from "@/pages/protected/ContactRequests.jsx";
import Notifications from "@/pages/protected/Notifications.jsx";
import FoundationMembersReport from "@/pages/protected/FoundationMembersReport.jsx";
import useAuth from "@/hooks/stores/useAuth";

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route
      errorElement={
        <GlobalErrorElement
          error={new Error("Page Not Found")}
          resetErrorBoundary={() => { }}
        />
      }
    >
      {/* Error boundry and Layout is defined here */}
      <Route
        element={
          <ErrorBoundary FallbackComponent={GlobalErrorElement}>
            <Layout />
          </ErrorBoundary>
        }
      >
        {/* Protected Routes */}
        <Route element={<ProtectedRoute />}>
          {/* Candidate-only routes */}
          <Route element={<RoleBasedRoute allowedRoles={['candidate']} />}>
            <Route path="/candidate/dashboard" element={<CandidateDashboard />} />
            <Route path="/candidate/applications" element={<CandidateApplications />} />
            <Route path="/candidate/applications/:applicationId" element={<CandidateApplicationView />} />
            <Route path="/candidate/notifications" element={<Notifications />} />
          </Route>

          {/* Company-only routes */}
          <Route element={<RoleBasedRoute allowedRoles={['company', 'admin', 'company_admin', 'recruiter']} />}>
            <Route path="/dashboard/:companySlug?" element={<CompanyDashboard />}  />
            <Route path="/recruiter-admin-dashboard" element={<RecruiterAdminDashboard />} />
          </Route>

          {/* Admin and company_admin routes */}
          <Route element={<RoleBasedRoute allowedRoles={['admin', 'company_admin']} />}>
            <Route path="/admin-dashboard" element={<AdminDashboard />} />
            <Route path="/forms" element={<Forms />} />
            <Route path="/users" element={<Users />} />
            <Route path="/recruiters" element={<Recruiters />} />
            <Route path="/settings" element={<SettingsList />} />
            <Route path="/personal-form-fields" element={<AdminDashboard />} />
          </Route>

          {/* Admin-only routes */}
          <Route element={<RoleBasedRoute allowedRoles={['admin']} />}>
            <Route path="/marketing" element={<Marketing />} />
            <Route
              path="/marketing/campaigns/create"
              element={<CreateMarketingCampaign />}
            />
            <Route
              path="/marketing/campaigns/:id/edit"
              element={<CreateMarketingCampaign />}
            />
            <Route path="/vacancy/Statuses" element={<VacancyStatuses />} />
            <Route path="/vacancy/Statuses/builder/:id?" element={<StageBuilder />} />
          </Route>

          {/* Routes for all authenticated users except company */}
          <Route element={<RoleBasedRoute allowedRoles={['admin', 'company_admin', 'recruiter']} />}>
            <Route path="/" element={<Companies />} />
            <Route path="/companies" element={<Companies />} />
            <Route path="/candidate" element={<Candidates />} />
            <Route path="/candidate/:candidate_id" element={<CandidateDetails />} />
            <Route path="/companies/add" element={<CompanyForm />} />
            <Route path="/companies/edit/:id" element={<CompanyForm />} />
            <Route path="/companies/view/:id" element={<CompanyView />} />
            <Route path="/companies/:companyId/add-campaign" element={<CampaignForm />} />
            <Route path="/campaigns/:id" element={<CampaignForm />} />
            <Route path="/campaigns/:id/view" element={<CampaignForm readOnly />} />
            <Route path="/vacancies" element={<Vacancies />} />
            <Route path="/vacancies/add" element={<VacancyForm />} />
            <Route path="/forms/view/:id" element={<FormView />} />
            <Route path="/forms/builder/:id?" element={<FormBuilder />} />
            <Route path="/campaigns/:campaignId/vacancies" element={<CampaignVacancies />} />
            <Route path="/campaigns/:campaignId/vacancies/add" element={<VacancyForm />} />
            <Route path="/campaigns/:campaignId/vacancies/:vacancyId" element={<VacancyForm />} />
            <Route path="/campaigns/:campaignId/vacancies/:vacancyId/delete" element={<VacancyForm />} />
            <Route path="/archived/campaigns" element={<ArchivedCampaign />} />
            <Route path="/all/campaigns" element={<AllCampaign />} />
            <Route path="/archived/vacancies" element={<ArchivedVacancy />} />
            <Route path="/all/vacancies" element={<AllVacancies />} />
            <Route path="/notes" element={<Notes />} />
            <Route path="/note-templates" element={<NoteTemplates />} />
            <Route path="/interviews-slots" element={<InterviewSlotsUpload />} />
            <Route path="/interviews-slots/create" element={<CreateEventSlot />} />
            <Route path="/interviews-slots/:vacancyId/:statusId" element={<InterviewSlotDetails />} />
            <Route path="/contact-requests" element={<ContactRequests />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route
              path="/foundation-members"
              element={<FoundationMembersReport />}
            />
          </Route>
        </Route>

        {/* Public Routes - Authenticated users will be redirected to home */}
        <Route element={<NotProtectedOnlyRoutes />}>
          <Route path="login" element={<Login />} />
          <Route path="auth/verify-otp" element={<VerifyOtp />} />
          <Route path="auth/forgot-password" element={<ForgotPassword />} />
          <Route path="auth/reset-password/:token" element={<ResetPassword />} />
        </Route>

        {/* Public Routes - Accessible to all */}
        <Route path="about" element={<About />} />
        <Route path="contact" element={<Contact />} />
      </Route>

      <Route
        element={
          <ErrorBoundary FallbackComponent={GlobalErrorElement}>
            <FullScreenLayout />
          </ErrorBoundary>
        }
      >
        {/* Protected Routes */}
        <Route element={<ProtectedRoute />}>
          <Route path="/vacancies/:vacancyId/pre-screen" element={<PreScreenView />} name="preScreen" />
          <Route path="/vacancies/:vacancyId/applications/:applicationId" element={<ApplicationReviewContainer />} name="applicationView" />
        </Route>
      </Route>

      {/** Public Routes - Frontend */}
      <Route path="/404" element={<NotFound />} />
      <Route path="/application/success" element={<SuccessPage />} />

      <Route path="/:companySlug/:campaignSlug" element={<FrontLayout />} />
      <Route path="/:companySlug/:campaignSlug/:vacancySlug" element={<ApplyForm />} />
      <Route path="/:vacancySlug" element={<ApplyForm />} />

    </Route>
  )
);
